
import EazyHeading from '@/components/EazyHeading.vue';
import UserRow from '@/components/UserRow.vue';
import requestAPI from '@/logic/requestBeApi';
import getMeetingDateAndTime from '@/logic/helpers/dates/getMeetingDateAndTime';
import FileUploader from '@/components/Inputs/FileUploader.vue';
import store from '@/store/index';
import router from '@/router/index';
import exitModal from '@/logic/exitModal';
import formValidators from '@/logic/helpers/forms/validators';
import inputValidators from '@/logic/helpers/inputs/validators';

export default {
  name: 'AssessorReport',

  components: {
    EazyHeading,
    UserRow,
    FileUploader,
  },

  props: {
    inModal: Boolean,
    candidateIdFromModal: {
      type: String,
      default: '',
    },
    assessmentIdFromModal: {
      type: String,
      default: '',
    },
    positionFromModal: {
      type: String,
      default: '',
    },
    companyFromModal: {
      type: String,
      default: '',
    },
    statusFromModal: {
      type: String,
      default: '',
    },
  },

  data(): Record<string, unknown> {
    return {
      router,
      getMeetingDateAndTime,
      assessmentReport: {
        fitsCompany: undefined,
        recommendationDescription: '',
        reportFile: '',
      },
      validationRules: {
        fitsCompany: [{ validator: inputValidators.fitsCompany, trigger: 'change' }],
        recommendationDescription: [{ validator: inputValidators.primitive, trigger: 'change' }],
        reportFile: [{ validator: inputValidators.primitive, trigger: 'change' }],
      },
      clientReportReview: {
        reportRejectionComment: '',
      },
      reportReviewValidationRules: {
        reportRejectionComment: [{ validator: inputValidators.primitive, trigger: 'change' }],
      },
      device: store.state.device,
      userRole: store.state.userRole,
      processingReport: false,
      fetchingCandidate: false,
      candidate: {
        first_name: '',
        surname: '',
      },
      decisions: [
        { desc: this.$t('yes'), bool: true },
        { desc: this.$t('no'), bool: false },
      ],
      meetingDate: '',
      clearFile: false,
      reportStatus: '',
      fetchedReportData: '',
      reportId: '',
      clientDecisionCheck: false,
      clientDecision: '',
      assessorWantsToCreateNewReport: false,
      formIsValid: false,
      cebirSessions: [],
      cebirSession: '',
    };
  },

  computed: {
    completedCebirSessionsCount(): number {
      return this.getCebirSessionsCount('completed');
    },
    newCebirSessionsCount(): number {
      return this.getCebirSessionsCount('new');
    },
    inProgressCebirSessionsCount(): number {
      return this.getCebirSessionsCount('in_test');
    },
    candidateId(): string {
      return this.$route.query.candidateID || this.candidateIdFromModal;
    },

    assessmentId(): string {
      return this.$route.query.assessmentID || this.assessmentIdFromModal;
    },

    company(): string {
      return this.$route.query.company || this.companyFromModal;
    },

    position(): string {
      return this.$route.query.position || this.positionFromModal;
    },

    status(): string {
      return this.$route.query.status || this.statusFromModal;
    },

    assessorCanCreateReport(): boolean {
      return (
        (this.status !== 'published' &&
          this.status !== 'completed' &&
          this.status !== 'in_review' &&
          this.userRole !== 'client') ||
        (this.fetchedReportData.status === 'rejected' && this.assessorWantsToCreateNewReport)
      );
    },

    userCanSeeSentReport(): boolean {
      return (
        this.status === 'published' ||
        this.status === 'completed' ||
        this.status === 'in_review' ||
        this.userRole === 'client' ||
        (this.fetchedReportData.status === 'rejected' && !this.assessorWantsToCreateNewReport)
      );
    },

    canCreateDraft(): boolean {
      return this.status === 'assessed' || this.status === 'published';
    },

    candidateFitsCompanyColor(): string {
      return this.displayDecision() === this.$t('yes') ? 'color: #2BC358' : 'color: #F56C6C';
    },

    candidateFitsCompanyIcon(): string {
      return this.displayDecision() === this.$t('yes') ? 'fas fa-check' : 'fas fa-times';
    },

    assessmentReportDataMissing(): boolean {
      return Object.values(this.assessmentReport).some((value) => !value && value !== false);
    },

    hasCebirSessions(): boolean {
      return this.cebirSessions.length > 0;
    },
  },

  watch: {
    candidateId(): void {
      this.getNeededData(true);
    },

    assessmentId(): void {
      this.getNeededData(true);
    },

    status(): void {
      this.getNeededData();
    },

    reportStatus(): void {
      this.getNeededData();
    },
  },

  created(): void {
    this.getNeededData();
  },

  methods: {
    getCebirSessionsCount(status): number {
      return this.cebirSessions.filter((session) => session.status === status).length;
    },
    getNeededData(clearPreviousData: boolean): void {
      if (this.doNotSendGetRequests) return;

      this.doNotSendGetRequests = true;

      if (clearPreviousData) this.clearData();
      this.getCandidate();
      this.getMeeting();
      this.getReport();

      setTimeout(() => {
        this.doNotSendGetRequests = false;
      });
    },

    handleFileUpload(payload: unknown): void {
      this.assessmentReport.reportFile = payload;
      this.clearFile = false;
    },

    async getCandidate(): Promise<void> {
      if (!this.candidateId) return;
      this.fetchingCandidate = true;

      const resp = await requestAPI({
        method: 'GET',
        url: `/api/v1/candidates/${this.candidateId}`,
      });

      if (resp.ok) {
        const data = await resp.json();
        this.candidate = data;

        if (!this.candidate.profile_picture) {
          this.candidate.profile_picture = {
            url: require('@/assets/default-avatar.png'),
          };
        }
        if (data.cebir_sessions) {
          this.cebirSessions = data.cebir_sessions;
          this.cebirSessions.forEach((session) => {
            if (session.status === 'completed') {
              this.cebirSession = session;
              return;
            }
          });
        }
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.fetchingCandidate = false;
    },

    getSessionStatus(status): string {
      if (status === 'new') {
        return this.$t('new');
      } else if (status === 'in_test') {
        return this.$t('inProgress');
      } else if (status === 'completed') {
        return this.$t('completed');
      } else {
        return status;
      }
    },

    async getMeeting(): Promise<void> {
      if (!this.candidateId) return;
      this.fetchingCandidate = true;

      const resp = await requestAPI({
        method: 'GET',
        url: `/api/v1/assessments/${this.assessmentId}`,
      });

      if (resp.ok) {
        const data = await resp.json();
        this.meetingDate = data.date;
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.fetchingCandidate = false;
    },

    async publishReport(reportStatus: string): Promise<void> {
      this.reportStatus = reportStatus;

      formValidators.validateForm(this, 'assessmentReportForm');

      if (!this.formIsValid) return;

      this.processingReport = true;

      const payload = {
        assessment: `/api/v1/assessments/${this.assessmentId}`,
        fits_company: this.assessmentReport.fitsCompany,
        comments: this.assessmentReport.recommendationDescription,
        report_file: this.assessmentReport.reportFile,
        status: this.reportStatus,
      };

      if (this.status === 'draft') delete payload.report_file;

      let resp: Response;

      if (this.status !== 'draft' && this.fetchedReportData.status !== 'rejected') {
        resp = await requestAPI({
          method: 'POST',
          url: '/api/v1/reports',
          payload,
        });
      } else {
        resp = await requestAPI({
          method: 'PATCH',
          url: `/api/v1/reports/${this.reportId}`,
          contentType: 'application/merge-patch+json',
          payload,
        });
      }

      if (resp.ok) {
        this.reportStatus === 'published'
          ? this.$message.success(this.$t('reportPublished'))
          : this.$message(this.$t('draftSaved'));

        setTimeout(() => {
          this.assessorWantsToCreateNewReport = false;
        });

        if (this.inModal) {
          this.status === 'confirmed' ? store.commit('setReloadSpecificList', 'interviews') : null;

          exitModal('reports', true);
        } else {
          router.push('/assessor-tasks');
        }
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.processingReport = false;
    },

    async getReport(): Promise<void> {
      if (
        this.status !== 'draft' &&
        this.status !== 'in_review' &&
        this.status !== 'published' &&
        this.status !== 'completed'
      ) {
        return;
      }

      this.processingReport = true;

      const resp = await requestAPI({
        method: 'GET',
        url: `/api/v1/assessments/${this.assessmentId}`,
      });

      if (resp.ok) {
        await resp
          .json()
          .then(async (data) => {
            const resp = await requestAPI({
              method: 'GET',
              url: `/api/v1/reports/${data.report.id}`,
            });

            this.reportId = data.report.id;

            return resp;
          })
          .then(async (fetchedReportData) => {
            fetchedReportData = await fetchedReportData.json();
            this.fetchedReportData = fetchedReportData;
          })
          .catch((err: Error) => err);

        if (this.status === 'draft' || this.fetchedReportData.status === 'draft') {
          this.assessmentReport.fitsCompany = this.fetchedReportData.fits_company;
          this.assessmentReport.recommendationDescription = this.fetchedReportData.comments;
          this.assessmentReport.reportFile = this.fetchedReportData.report_file;
        }
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.processingReport = false;
    },

    clearData(): void {
      this.assessmentReport.fitsCompany = undefined;
      this.assessmentReport.recommendationDescription = '';
      this.assessmentReport.reportFile = '';
      this.clearFile = true;
      this.fetchedReportData = '';
    },

    displayDecision(): string {
      return this.fetchedReportData.fits_company ? this.$t('yes') : this.$t('no');
    },

    async clientDecideOnReport(decision: string): Promise<void> {
      if (decision === 'reject') {
        formValidators.validateForm(this, 'clientReportReviewForm');

        if (!this.formIsValid) return;
      }

      this.clientDecisionCheck = false;
      this.processingReport = true;

      let payload = {};

      if (decision === 'reject') {
        payload = {
          comment: this.clientReportReview.reportRejectionComment,
        };
      }

      const resp = await requestAPI({
        url: `/api/v1/reports/${this.reportId}/${decision}`,
        contentType: 'application/merge-patch+json',
        method: 'PATCH',
        payload,
      });

      if (resp.ok) {
        decision === 'accept'
          ? this.$message.success(this.$t('clientAcceptedReport'))
          : this.$message(this.$t('clientDeclinedReport'));
        this.$emit('changeStage');

        if (!this.inModal) {
          setTimeout(() => {
            router.push('/client-tasks');
          }, 1000);
        }
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.processingReport = false;
    },

    checkIfUserDecisionIsFinal(decision: string): void {
      this.clientDecisionCheck = true;
      this.clientDecision = decision;
    },
  },
};
